import React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import EditableFunnelName from "@/features/funnel/components/EditableFunnelName";
import { PublishButton } from "@/features/next-gen/components/PublishButton";
import ManageChats from "@/features/react-editor/components/ManageChats";
import PageSelectionDropdown from "@/features/react-editor/components/navbar/PageSelectionDropdown";
import ResponsiveControls from "@/features/react-editor/components/navbar/ResponsiveControls";
import { useProjectPresences } from "@/features/react-editor/components/ProjectWebsocketProvider";
// import { useDebugStore } from "@/features/react-editor/store/debugStore";
import { useCurrentFile } from "@/features/react-editor/utils/useCurrentFile";
import { useWebContainer } from "@/features/web-container/context";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Popover from "@replo/design-system/components/popover/Popover";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, SquareArrowOutUpRight } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

function ReactEditorNavbar() {
  // const { isDebugModeOn, setIsDebugModeOn } = useDebugStore();
  const { project } = useCurrentProjectContext();
  const file = useCurrentFile();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRenamingFunnel, setIsRenamingFunnel] = React.useState(false);
  const [isPageSelectionPopoverOpen, setIsPageSelectionPopoverOpen] =
    React.useState(false);

  // Get route params to determine which data to fetch
  const { pageId, funnelId } = useParams();
  const presences = useProjectPresences({ pageId, funnelId });

  const isFunnelRoute = Boolean(project?.id && funnelId);
  const isPageRoute = Boolean(project?.id && pageId);

  const { logs } = useWebContainer();
  const [showLogs, setShowLogs] = React.useState(false);

  // Conditionally fetch funnel data if we're on a funnel route
  const { data: funnel } = trpc.funnel.find.useQuery(
    project?.id && funnelId
      ? {
          projectId: project.id,
          funnelId,
        }
      : skipToken,
    {
      enabled: isFunnelRoute,
    },
  );

  // Conditionally fetch page data if we're on a page route
  const { data: page } = trpc.nextJSProject.getPageComponent.useQuery(
    project?.id && pageId
      ? {
          projectId: project.id,
          id: pageId,
        }
      : skipToken,
    {
      enabled: isPageRoute,
    },
  );

  // Determine the display name based on current route
  const displayName = React.useMemo(() => {
    if (funnelId && funnel) {
      return funnel.name;
    }
    if (pageId && page) {
      return page.pageRoute;
    }
    return null;
  }, [funnelId, funnel, pageId, page]);

  const { mutate: resetFiles, isPending: isLoadingFilesReset } =
    trpc.nextJSProject.initializeFiles.useMutation({
      onSuccess: async (_, { projectId }) => {
        await trpcUtils.webContainer.getFileTree.invalidate({
          projectId,
        });
        await trpcUtils.funnel.list.invalidate({
          projectId,
        });
        navigate(`/shops/${projectId}/funnels`);
      },
    });

  const { data: customDomainData } =
    trpc.v2CustomDomain.getByProjectId.useQuery(
      { projectId: project?.id ?? "" },
      { enabled: Boolean(project?.id) },
    );

  const firstCustomDomain = customDomainData?.customDomains[0];

  const pageRouteToUseForPreview = isFunnelRoute
    ? funnel?.funnelSteps[0]?.file?.pageRoute ?? ""
    : file?.pageRoute ?? "";

  const reploShopsUrl = project?.shopDomainPrefix
    ? `https://${project.shopDomainPrefix}.reploshops.com${pageRouteToUseForPreview}`
    : undefined;

  const publishedSiteUrl = firstCustomDomain?.value
    ? `https://${firstCustomDomain.value}${pageRouteToUseForPreview}`
    : reploShopsUrl;

  const shouldShowV2Debuggers =
    localStorage.getItem("replo.debug.v2debuggers") === "true";

  // Smart navigation logic for back button
  const handleBackNavigation = React.useCallback(() => {
    const currentPath = location.pathname;

    // Check if we have meaningful browser history by checking if location.key exists
    // location.key is undefined on the initial load, indicating no history
    const hasHistory = location.key !== "default";

    if (!project) {
      navigate("/shops");
      return;
    }

    // If we're in a funnel route, go back to funnels list
    if (currentPath.includes("/funnels") && funnelId) {
      navigate(`/shops/${project.id}/funnels`);
      return;
    }

    // If we're in a site route, first check if we can go back to previous page, otherwise go to homepage
    // TODO: go to /site tab when it's ready for v2
    if (currentPath.includes("/site") && pageId) {
      if (hasHistory) {
        navigate(-1);
      } else {
        navigate(`/shops/${project.id}`);
      }
      return;
    }
  }, [location.pathname, location.key, navigate, project, funnelId, pageId]);

  const isSiteRoute = location.pathname.includes("/site");

  const logsRef = React.useRef<HTMLPreElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to scroll to the bottom of the logs when they change
  React.useEffect(() => {
    if (logsRef.current) {
      logsRef.current?.scrollTo({
        top: logsRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [logs]);

  const isFunnelEditor = funnelId && funnel && project;
  const isPageEditor = pageId && page && project;

  const renderDisplayName = () => {
    if (isFunnelEditor) {
      return (
        <EditableFunnelName
          funnel={funnel}
          projectId={project.id}
          isRenaming={isRenamingFunnel}
          setIsRenaming={setIsRenamingFunnel}
          inputClassName="max-w-[200px]"
          titleClassName="text-muted text-base font-medium cursor-pointer hover:text-default transition-colors line-clamp-1"
        />
      );
    }

    if (isPageEditor) {
      return (
        <Popover
          isOpen={isPageSelectionPopoverOpen}
          onOpenChange={setIsPageSelectionPopoverOpen}
          hideCloseButton
          align="center"
          side="bottom"
          triggerAsChild
          content={
            <PageSelectionDropdown
              projectId={project.id}
              handlePageSelectionCardClick={(page) => {
                navigate(`/shops/${project.id}/site/${page.id}`);
                setIsPageSelectionPopoverOpen(false);
              }}
              currentPageId={pageId}
            />
          }
          shouldPreventDefaultOnInteractOutside={false}
          className="ml-3 w-[270px] max-h-[600px] overflow-y-auto"
        >
          <div className="flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors hover:bg-selectable-hover">
            <span className="header-h4 line-clamp-1">
              {page.name ?? "Untitled Page"}
            </span>
            <ChevronDown size={10} />
          </div>
        </Popover>
      );
    }

    return (
      <div className="text-muted text-base font-medium">
        {displayName ?? ""}
      </div>
    );
  };

  return (
    <>
      <div className="flex gap-3 w-full">
        <div
          className={twMerge(
            "max-w-[260px] w-full",
            isSiteRoute && "max-w-1/3",
          )}
        >
          <div className="flex flex-row items-center gap-3">
            <IconButton
              variant="secondary"
              size="base"
              onClick={handleBackNavigation}
              icon={<ArrowLeft className="text-muted" size={16} />}
            />
            {renderDisplayName()}
          </div>
        </div>
        <ResponsiveControls />
        <div className="ml-auto flex gap-3">
          {shouldShowV2Debuggers && (
            <>
              <ManageChats />
              {/* <div
                className="flex items-center gap-2"
                onClick={() => setIsDebugModeOn(!isDebugModeOn)}
              >
                <Switch
                  id="debug-toggle"
                  isOn={isDebugModeOn}
                  size="base"
                  aria-labelledby="debug-toggle-label"
                />
                <span
                  id="debug-toggle-label"
                  className="cursor-pointer text-sm select-none"
                >
                  View Source Code
                </span>
              </div> */}
              <Button
                id="reset-files-toggle"
                onClick={() =>
                  project &&
                  resetFiles({
                    projectId: project.id,
                  })
                }
                variant="secondary"
                size="base"
                isLoading={isLoadingFilesReset}
              >
                Reset Files
              </Button>
              <Button
                onClick={() => setShowLogs(!showLogs)}
                variant="secondary"
                size="base"
              >
                {showLogs ? "Hide WebContainer Logs" : "Show WebContainer Logs"}
              </Button>
            </>
          )}
          <div className="px-2 flex items-center">
            {presences.map((presence) => (
              <Tooltip
                key={presence.userId}
                content={
                  <div className="flex flex-col gap-1 items-center p-1">
                    <span className="text-sm font-medium">
                      {presence.userName}
                    </span>
                    <span className="text-xs">is also editing</span>
                  </div>
                }
                side="bottom"
                sideOffset={0}
                triggerAsChild
              >
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-[14px] text-white select-none border border-white hover:rotate-15 transition-transform duration-300"
                  style={{
                    backgroundColor: presence.color,
                    marginRight: "-4px",
                  }}
                >
                  {presence.userName.charAt(0).toUpperCase()}
                </div>
              </Tooltip>
            ))}
          </div>
          <Button
            variant="secondary"
            size="base"
            to={publishedSiteUrl}
            target="_blank"
            layoutClassName="w-full flex-1"
            title={publishedSiteUrl}
            disabled={!Boolean(publishedSiteUrl)}
          >
            <div className="flex items-center gap-2">
              <SquareArrowOutUpRight size={16} /> View Live
            </div>
          </Button>
          <PublishButton
            routeType={isFunnelRoute ? "funnel" : "page"}
            publishedSiteUrl={publishedSiteUrl}
            showAddDomainButton={!Boolean(firstCustomDomain)}
          />
        </div>
      </div>
      {showLogs && (
        <pre
          ref={logsRef}
          // eslint-disable-next-line replo/no-tailwind-literal-color
          className="fixed z-10 bottom-0 left-0 right-0 h-96 overflow-x-auto overflow-y-auto rounded bg-black p-2 text-xs text-green-400"
        >
          {logs.map((log, index) => (
            <div key={index}>
              {log.date.toLocaleTimeString()} {log.message}
            </div>
          ))}
        </pre>
      )}
    </>
  );
}

export default ReactEditorNavbar;
